import { createSceneSpecFieldConverter, sceneSpecFormFromSpec, type SceneSpecFormValue, type SceneSpecPreserver } from '../../../../src/lib/agent/scene-gen-spec-core';
import type { NativeSceneSpec, SceneSpecTarget, SceneGenSpec } from '../../protocol/source-scene-generation';

export type SourceSceneReferenceSlot = 'start' | 'location' | 'ai' | 'consistency';
export type SourceSceneImageAsset = { id: string; type: string; remoteUrl?: string | null; filename?: string; label?: string };
export type SourceSceneReferenceSelections = Partial<Record<SourceSceneReferenceSlot, string | null>>;
export type SourceSceneSpecCapture = { saved: NativeSceneSpec; target: SceneSpecTarget; documentIdentity: object };
/** A draft, not a document transaction. Parent validates duration/authority and commits one Undo. */
export type SourceSceneSpecDraftPatch = SourceSceneSpecCapture & {
    form: SceneSpecFormValue;
    referenceSelections: SourceSceneReferenceSelections;
    /** Ephemeral UI intent; undefined means untouched, empty string means explicit seed removal. */
    seedEdit?: string;
    capsuleBase?: SceneGenSpec;
    previewCapsule?: SceneGenSpec;
};
export type SourceSceneSpecSaveResult =
    | { committed: true; saved: NativeSceneSpec; target: SceneSpecTarget; documentIdentity: object; durationIssue?: string | null }
    | { committed: false; reason: string };

export function sameSourceSceneTarget(a: SceneSpecTarget, b: SceneSpecTarget): boolean {
    return a.scope.projectId === b.scope.projectId && a.scope.workId === b.scope.workId && a.scope.surface === b.scope.surface
        && (['revision', 'itemId', 'itemLineage', 'sceneId', 'ownerItemId', 'documentSignature', 'itemSignature', 'sceneSignature'] as const).every(key => a[key] === b[key]);
}
export function sourceSceneCaptureIsCurrent(capture: SourceSceneSpecCapture, target: SceneSpecTarget, documentIdentity: object): boolean {
    return capture.documentIdentity === documentIdentity && sameSourceSceneTarget(capture.target, target);
}
export function sourceSceneSelectableImage(asset: SourceSceneImageAsset): boolean {
    if (asset.type !== 'image' || !asset.id || !asset.remoteUrl) return false;
    try { const url = new URL(asset.remoteUrl); return url.protocol === 'https:' && !url.username && !url.password; }
    catch { return false; }
}
export function sourceSceneReferenceUrl(spec: SceneGenSpec | undefined, slot: SourceSceneReferenceSlot): string | undefined {
    if (slot === 'start') return spec?.startAnchor?.kind === 'upload' ? spec.startAnchor.url : undefined;
    if (slot === 'consistency') return spec?.consistency?.anchorUrl;
    return slot === 'location' ? spec?.locationRef?.url : spec?.aiRef?.url;
}
/** Asset IDs are resolved only against current native inventory. No arbitrary URL text input. */
export function buildSourceSceneSpecDraftPatch(capture: SourceSceneSpecCapture, form: SceneSpecFormValue,
    selections: SourceSceneReferenceSelections, assets: readonly SourceSceneImageAsset[], preserveSceneGenSpec: SceneSpecPreserver,
    seedEdit?: string): SourceSceneSpecDraftPatch {
    const base: SceneGenSpec = structuredClone(capture.saved.capsule ?? {});
    const inheritedConsistency = { ...capture.saved.regenFields?.consistency, ...base.consistency };
    if (Object.keys(inheritedConsistency).length) base.consistency = inheritedConsistency;
    for (const slot of ['start', 'location', 'ai', 'consistency'] as const) {
        const selection = selections[slot];
        if (selection === undefined) continue;
        let asset: SourceSceneImageAsset | undefined;
        if (selection !== null) {
            const matches = assets.filter(value => value.id === selection && sourceSceneSelectableImage(value));
            if (matches.length !== 1) throw Error('연결할 이미지를 현재 자료함에서 다시 선택해 주세요.');
            asset = matches[0];
        }
        if (slot === 'start') {
            if (asset) base.startAnchor = { kind: 'upload', url: asset.remoteUrl! };
            else if (base.startAnchor?.kind === 'upload') delete base.startAnchor;
        } else if (slot === 'consistency') {
            const consistency = { ...base.consistency };
            if (asset) consistency.anchorUrl = asset.remoteUrl!;
            else delete consistency.anchorUrl;
            if (Object.keys(consistency).length) base.consistency = consistency;
            else delete base.consistency;
        } else {
            const key = slot === 'location' ? 'locationRef' : 'aiRef';
            if (asset) base[key] = { url: asset.remoteUrl!, ...(asset.label || asset.filename ? { label: asset.label || asset.filename } : {}) };
            else delete base[key];
        }
    }
    if (seedEdit !== undefined) {
        const consistency = { ...base.consistency };
        if (!seedEdit.trim()) delete consistency.seed;
        else {
            const seed = Number(seedEdit);
            if (!/^\d+$/.test(seedEdit.trim()) || preserveSceneGenSpec({ consistency: { seed } }).spec?.consistency?.seed !== seed)
                throw Error('일관성 번호를 유효한 정수로 입력하거나 비워 주세요.');
            consistency.seed = seed;
        }
        if (Object.keys(consistency).length) base.consistency = consistency;
        else delete base.consistency;
    }
    if (form.startAnchorKind === 'upload' && !base.startAnchor?.url) throw Error('시작 이미지 한 장을 자료함에서 선택해 주세요.');
    const preserved = preserveSceneGenSpec(base);
    if (preserved.droppedKeys.length) throw Error('보존할 수 없는 씬 설정이 있습니다. 설정을 다시 확인해 주세요.');
    const sceneSpecToSpec = createSceneSpecFieldConverter(preserveSceneGenSpec);
    // Invalid numeric duration is kept in form for the parent's partial-save response.
    const previewForm = form.durationSec !== null && (!Number.isFinite(form.durationSec) || form.durationSec <= 0)
        ? { ...form, durationSec: sceneSpecFormFromSpec(capture.saved.capsule).durationSec } : form;
    return { ...capture, target: structuredClone(capture.target), saved: structuredClone(capture.saved), form: { ...form }, referenceSelections: { ...selections }, ...(seedEdit !== undefined ? { seedEdit } : {}),
        capsuleBase: preserved.spec, previewCapsule: sceneSpecToSpec(previewForm, preserved.spec) };
}
